import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { publisherService } from "../services/publisherService";
import {
  PublisherValuesInit,
  getPublisherUpdateVariables,
  getPublisherValues,
  PublisherValuesType,
  PublisherErrorsType,
  publisherDisplayId,
} from "./PublisherCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { Publisher, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getCountryValues,
  CountryValuesType,
  countryDisplayId,
} from "../../country/store/CountryCommonStore";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { validateMask } from "../../common/utils";
import { normalizeAndValidateEmail } from "../../../common/utils";
import {
  getProvinceStateValues,
  ProvinceStateValuesType,
  provinceStateDisplayId,
} from "../../provinceState/store/ProvinceStateCommonStore";
import { PublisherRepresentativeUpdateStore } from "../../publisherRepresentative/store";
import { PublisherCollectingSocietyUpdateStore } from "../../publisherCollectingSociety/store";
import { GridChangeSet } from "../../common/types";
import { PersonType } from "../../../api/graphql";
import { MaritalStatus } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class PublisherUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: PublisherErrorsType = {};
  service = publisherService;
  publisherInstance: Publisher | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    basicInformation: { open: true, errors: [] },
    address: { open: true, errors: [] },
    legalInformation: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = PublisherValuesInit();
  personTypeError: string | undefined;
  nameError: string | undefined;
  maritalStatusError: string | undefined;
  nationalityError: string | undefined;
  nationalityService = countryService;
  nationalitySearchValue: string = "";
  nationalityOptions: CountryValuesType[] = [];
  pseudonymError: string | undefined;
  taxIdError: string | undefined;
  phoneError: string | undefined;
  emailError: string | undefined;
  streetAndNumberError: string | undefined;
  floorError: string | undefined;
  suiteError: string | undefined;
  postalCodeError: string | undefined;
  countryError: string | undefined;
  countryService = countryService;
  countrySearchValue: string = "";
  countryOptions: CountryValuesType[] = [];
  provinceError: string | undefined;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceOptions: ProvinceStateValuesType[] = [];
  cityError: string | undefined;
  caeIpiError: string | undefined;
  representativesError: string | undefined;
  representativesVisibleColumns: { name: string; title: string }[];
  representativesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  representativesUpdateStore: PublisherRepresentativeUpdateStore;
  managementSocietiesError: string | undefined;
  managementSocietiesVisibleColumns: { name: string; title: string }[];
  managementSocietiesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  managementSocietiesUpdateStore: PublisherCollectingSocietyUpdateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<PublisherValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.representativesVisibleColumns = [
      { name: "name", title: this.langStore.PublisherRepresentativeNameLabel },
      { name: "role", title: this.langStore.PublisherRepresentativeRoleLabel },
    ];
    this.representativesColumnTypes = {
      name: { fieldType: "String" as const, nullable: false, readOnly: false },
      role: { fieldType: "String" as const, nullable: true, readOnly: false },
    };
    this.representativesUpdateStore = new PublisherRepresentativeUpdateStore(
      rootStore,
      "representatives_" + this.storeId
    );
    this.managementSocietiesVisibleColumns = [
      {
        name: "__collectingSociety",
        title: this.langStore.PublisherCollectingSocietyCollectingSocietyLabel,
      },
      {
        name: "cmsAccountNumber",
        title: this.langStore.PublisherCollectingSocietyCmsAccountNumberLabel,
      },
    ];
    this.managementSocietiesColumnTypes = {
      __collectingSociety: {
        fieldType: "__Reference" as const,
        nullable: false,
        readOnly: false,
        multi: false,
        onChange: (value: string) =>
          this.managementSocietiesUpdateStore.setCollectingSocietyValue(value),
        onSelect: (value: any) =>
          this.managementSocietiesUpdateStore.setSelectedCollectingSociety(
            value
          ),
        getOptions: () =>
          this.managementSocietiesUpdateStore.getCollectingSocietyOptions(),
      },
      cmsAccountNumber: {
        fieldType: "String" as const,
        nullable: false,
        readOnly: false,
      },
    };
    this.managementSocietiesUpdateStore =
      new PublisherCollectingSocietyUpdateStore(
        rootStore,
        "managementSocieties_" + this.storeId
      );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<PublisherValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }

    if (this.representativesUpdateStore) {
      this.representativesUpdateStore.destroy();
    }
    if (this.managementSocietiesUpdateStore) {
      this.managementSocietiesUpdateStore.destroy();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<PublisherValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadPublisherById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.publisherInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.publisherInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getPublisherValues(
        this.publisherInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadPublisher(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadPublisherById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return publisherDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.publisherInstance) {
      return publisherDisplayId(this.publisherInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PublisherSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "basicInformation",
        label: this.rootStore.langStore.groupPublisherBasicInformationLabel,
        errors: this.getCardErrors("basicInformation"),
      },
      {
        id: "address",
        label: this.rootStore.langStore.groupPublisherAddressLabel,
        errors: this.getCardErrors("address"),
      },
      {
        id: "legalInformation",
        label: this.rootStore.langStore.groupPublisherLegalInformationLabel,
        errors: this.getCardErrors("legalInformation"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupPublisherSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (callerData?.entityName === "PublisherRepresentative") {
        this.representativesUpdateStore.isWaitingForCallback = false;
        this.representativesUpdateStore.callbackReturnPath = null;
        yield this.representativesUpdateStore.returnedData(data);
        return;
      }
      if (callerData?.entityName === "PublisherCollectingSociety") {
        this.managementSocietiesUpdateStore.isWaitingForCallback = false;
        this.managementSocietiesUpdateStore.callbackReturnPath = null;
        yield this.managementSocietiesUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "nationality") {
        if (returnData.entity) {
          const displayId = countryDisplayIdByDemonym(
            returnData.entity,
            this.langStore
          );
          this.entityValues.showNationality = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({ id: returnData.resultId });
          const country = result.countryOne;
          this.entityValues.showNationality = {
            displayId: countryDisplayIdByDemonym(country, this.langStore),
            id: country.id,
          };
          this.setNationalityOptions([
            {
              ...getCountryValues(country, this.langStore),
              displayId: countryDisplayIdByDemonym(country, this.langStore),
            },
          ]);
        }
      } else if (callerData?.fieldName === "country") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            countryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showCountry = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({ id: returnData.resultId });
          const country = result.countryOne;
          this.entityValues.showCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setCountryOptions([getCountryValues(country, this.langStore)]);
        }
      } else if (callerData?.fieldName === "province") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            provinceStateDisplayId(returnData.entity, this.langStore);
          this.entityValues.showProvince = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getPersonTypeValue(): PersonType {
    return this.entityValues.personType;
  }

  setPersonTypeValue(value: PersonType) {
    this.entityValues.personType = value;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getMaritalStatusValue(): MaritalStatus | "" {
    return this.entityValues.maritalStatus;
  }

  setMaritalStatusValue(value: MaritalStatus | "") {
    this.entityValues.maritalStatus = value;
  }

  getMaritalStatusOptions() {
    return this.langStore.MaritalStatusOptions;
  }

  setNationalityOptions(values: CountryValuesType[]) {
    this.nationalityOptions = values;
  }

  *loadNationalityValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = (cache.getOptions("Country") as CountryValuesType[]).map(
      (x: any) => ({
        ...x,
        displayId: countryDisplayIdByDemonym(x, this.langStore),
      })
    );

    const searchValue = this.nationalitySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setNationalityOptions(filtered);
  }

  getNationalityOptions() {
    return this.nationalityOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setNationalityValue(value: string) {
    this.nationalitySearchValue = value;
    yield this.loadNationalityValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedNationality(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "nationality"
      );
    } else {
      if (value) {
        this.entityValues.showNationality = value;
        this.entityValues.__nationality = value.displayId;
      }
    }
  }

  getPseudonymValue(): string {
    return this.entityValues.pseudonym;
  }

  setPseudonymValue(value: string) {
    this.entityValues.pseudonym = value;
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  getPhoneValue(): string {
    return this.entityValues.phone;
  }

  setPhoneValue(value: string) {
    this.entityValues.phone = value;
  }

  getEmailValue(): string {
    return this.entityValues.email;
  }

  setEmailValue(value: string) {
    this.entityValues.email = value;
  }

  getStreetAndNumberValue(): string {
    return this.entityValues.streetAndNumber;
  }

  setStreetAndNumberValue(value: string) {
    this.entityValues.streetAndNumber = value;
  }

  getFloorValue(): string {
    return this.entityValues.floor;
  }

  setFloorValue(value: string) {
    this.entityValues.floor = value;
  }

  getSuiteValue(): string {
    return this.entityValues.suite;
  }

  setSuiteValue(value: string) {
    this.entityValues.suite = value;
  }

  getPostalCodeValue(): string {
    return this.entityValues.postalCode;
  }

  setPostalCodeValue(value: string) {
    this.entityValues.postalCode = value;
  }

  setCountryOptions(values: CountryValuesType[]) {
    this.countryOptions = values;
  }

  *loadCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.countrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setCountryOptions(filtered);
  }

  getCountryOptions() {
    return this.countryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCountryValue(value: string) {
    this.countrySearchValue = value;
    yield this.loadCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "country"
      );
    } else {
      if (value) {
        this.entityValues.showCountry = value;
        this.entityValues.__country = value.displayId;
        yield this.loadProvinceValues();
        this.entityValues.showProvince = null;
        this.entityValues.__province = "";
      }
    }
  }

  setProvinceOptions(values: ProvinceStateValuesType[]) {
    this.provinceOptions = values;
  }

  *loadProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setProvinceOptions([]);
    }
  }

  getProvinceOptions() {
    return this.provinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setProvinceValue(value: string) {
    this.provinceSearchValue = value;
    yield this.loadProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "province"
      );
    } else {
      if (value) {
        this.entityValues.showProvince = value;
        this.entityValues.__province = value.displayId;
      }
    }
  }

  getCityValue(): string {
    return this.entityValues.city;
  }

  setCityValue(value: string) {
    this.entityValues.city = value;
  }

  getCaeIpiValue(): string {
    return this.entityValues.caeIpi;
  }

  setCaeIpiValue(value: string) {
    this.entityValues.caeIpi = value;
  }

  private get representativeshasChanges() {
    return (
      this.entityValues.showRepresentatives?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  representativesLoadRow(row: any) {
    this.representativesUpdateStore.loadValuesFromRow(row);
  }

  representativesRestore(id: string) {
    this.entityValues.showRepresentatives =
      this.entityValues.showRepresentatives.map((item) =>
        item.id === id
          ? { ...item, __status__: restoredStatus(item.__status__) }
          : item
      );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  representativesValidateChanges(_changes: GridChangeSet) {
    const errors = this.representativesUpdateStore.validateData(
      this.representativesUpdateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.representativesUpdateStore.entityValues;
    const existingRows = this.entityValues.showRepresentatives || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: name, role
    const isDuplicate_name_role = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.name === currentValues.name &&
        row.role === currentValues.role &&
        (currentValues.name || currentValues.role)
    );
    if (isDuplicate_name_role) {
      errors.name = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.PublisherRepresentativeNameLabel,
      };
    }

    this.representativesUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  representativesCommitInlineChange(
    rowId: string,
    changes: Record<string, any>
  ) {
    this.entityValues.showRepresentatives =
      this.entityValues.showRepresentatives.map((item) =>
        item.id === rowId
          ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
          : item
      );
  }

  representativesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.representativesUpdateStore.entityValues;
      const hadChanges = this.representativesUpdateStore.pendingChanges;
      if (hadChanges) {
        const representatives = this.entityValues.showRepresentatives;
        for (
          let index = 0;
          index < this.entityValues.showRepresentatives.length;
          index++
        ) {
          if (representatives[index].id === values.id) {
            this.entityValues.showRepresentatives =
              this.entityValues.showRepresentatives.map((item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
              );
            break;
          }
        }
        this.representativesUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.representativesUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showRepresentatives) {
        this.entityValues.showRepresentatives = [];
      }
      this.entityValues.showRepresentatives = [
        ...this.entityValues.showRepresentatives,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.representativesUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const representatives = this.entityValues.showRepresentatives;
      for (
        let index = 0;
        index < this.entityValues.showRepresentatives.length;
        index++
      ) {
        if (representatives[index].id === id) {
          if (isOriginalCrudStatus(representatives[index].__status__)) {
            this.entityValues.showRepresentatives =
              this.entityValues.showRepresentatives.map((item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
              );
          } else {
            this.entityValues.showRepresentatives =
              this.entityValues.showRepresentatives.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.representatives);
    //   this.setCRUDRowsFromValues(newChanges, this.representativesUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.representatives = getNewCRUDRows(newChanges, this.entityValues.representatives, this.uniqueCounter++);
    // }
  }

  private get managementSocietieshasChanges() {
    return (
      this.entityValues.showManagementSocieties?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  managementSocietiesLoadRow(row: any) {
    this.managementSocietiesUpdateStore.loadValuesFromRow(row);
  }

  managementSocietiesRestore(id: string) {
    this.entityValues.showManagementSocieties =
      this.entityValues.showManagementSocieties.map((item) =>
        item.id === id
          ? { ...item, __status__: restoredStatus(item.__status__) }
          : item
      );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  managementSocietiesValidateChanges(_changes: GridChangeSet) {
    const errors = this.managementSocietiesUpdateStore.validateData(
      this.managementSocietiesUpdateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.managementSocietiesUpdateStore.entityValues;
    const existingRows = this.entityValues.showManagementSocieties || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: collectingSociety
    const isDuplicate_collectingSociety = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showCollectingSociety?.id ===
          currentValues.showCollectingSociety?.id &&
        currentValues.showCollectingSociety?.id
    );
    if (isDuplicate_collectingSociety) {
      errors.collectingSociety = {
        message: this.langStore.duplicateValueError,
        fieldLabel:
          this.langStore.PublisherCollectingSocietyCollectingSocietyLabel,
      };
    }

    this.managementSocietiesUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  managementSocietiesCommitInlineChange(
    rowId: string,
    changes: Record<string, any>
  ) {
    this.entityValues.showManagementSocieties =
      this.entityValues.showManagementSocieties.map((item) =>
        item.id === rowId
          ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
          : item
      );
  }

  managementSocietiesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.managementSocietiesUpdateStore.entityValues;
      const hadChanges = this.managementSocietiesUpdateStore.pendingChanges;
      if (hadChanges) {
        const managementSocieties = this.entityValues.showManagementSocieties;
        for (
          let index = 0;
          index < this.entityValues.showManagementSocieties.length;
          index++
        ) {
          if (managementSocieties[index].id === values.id) {
            this.entityValues.showManagementSocieties =
              this.entityValues.showManagementSocieties.map((item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
              );
            break;
          }
        }
        this.managementSocietiesUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.managementSocietiesUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showManagementSocieties) {
        this.entityValues.showManagementSocieties = [];
      }
      this.entityValues.showManagementSocieties = [
        ...this.entityValues.showManagementSocieties,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.managementSocietiesUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const managementSocieties = this.entityValues.showManagementSocieties;
      for (
        let index = 0;
        index < this.entityValues.showManagementSocieties.length;
        index++
      ) {
        if (managementSocieties[index].id === id) {
          if (isOriginalCrudStatus(managementSocieties[index].__status__)) {
            this.entityValues.showManagementSocieties =
              this.entityValues.showManagementSocieties.map((item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
              );
          } else {
            this.entityValues.showManagementSocieties =
              this.entityValues.showManagementSocieties.filter(
                (x) => x.id !== id
              );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.managementSocieties);
    //   this.setCRUDRowsFromValues(newChanges, this.managementSocietiesUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.managementSocieties = getNewCRUDRows(newChanges, this.entityValues.managementSocieties, this.uniqueCounter++);
    // }
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  addNewCountry(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Publisher",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/country/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewProvinceState(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Publisher",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/provinceState/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.publisherInstance = null;
    this.entityValues = PublisherValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      basicInformation: { open: true, errors: [] },
      address: { open: true, errors: [] },
      legalInformation: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.nationalitySearchValue = "";
    this.nationalityOptions = [];
    this.countrySearchValue = "";
    this.countryOptions = [];
    this.provinceSearchValue = "";
    this.provinceOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: PublisherValuesType): PublisherErrorsType {
    const errors: PublisherErrorsType = {};
    this.clearErrors();

    if (!values.personType) {
      errors.personType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPersonTypeLabel,
      };
      this.setCardError("basicInformation", errors.personType.message);
    }
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherNameLabel,
      };
      this.setCardError("basicInformation", errors.name.message);
    }
    if (!validateMask(values.taxId, { mask: "99-99999999-9" })) {
      errors.taxId = {
        message: this.langStore.maskedField("99-99999999-9"),
        fieldLabel: this.langStore.PublisherTaxIdLabel,
      };
    }
    values.taxId = values.taxId.trim();
    if (values.taxId === "") {
      errors.taxId = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherTaxIdLabel,
      };
      this.setCardError("basicInformation", errors.taxId.message);
    }
    values.phone = values.phone.trim();
    if (values.phone === "") {
      errors.phone = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPhoneLabel,
      };
      this.setCardError("basicInformation", errors.phone.message);
    }

    if (values.email) {
      const normalizedEmail = normalizeAndValidateEmail(values.email);
      if (normalizedEmail === null) {
        errors.email = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.PublisherEmailLabel,
        };
        this.setCardError("basicInformation", errors.email.message);
      }
    } else {
      errors.email = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherEmailLabel,
      };
      this.setCardError("basicInformation", errors.email.message);
    }

    values.streetAndNumber = values.streetAndNumber.trim();
    if (values.streetAndNumber === "") {
      errors.streetAndNumber = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherStreetAndNumberLabel,
      };
      this.setCardError("address", errors.streetAndNumber.message);
    }
    values.postalCode = values.postalCode.trim();
    if (values.postalCode === "") {
      errors.postalCode = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherPostalCodeLabel,
      };
      this.setCardError("address", errors.postalCode.message);
    }
    this.countryError = undefined;
    if (values.showCountry === null || values.showCountry.id === "") {
      errors.country = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCountryLabel,
      };
      this.setCardError("address", errors.country.message);
    }
    this.provinceError = undefined;
    if (values.showProvince === null || values.showProvince.id === "") {
      errors.province = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherProvinceLabel,
      };
      this.setCardError("address", errors.province.message);
    }
    values.city = values.city.trim();
    if (values.city === "") {
      errors.city = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCityLabel,
      };
      this.setCardError("address", errors.city.message);
    }
    values.caeIpi = values.caeIpi.trim();
    if (values.caeIpi === "") {
      errors.caeIpi = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherCaeIpiLabel,
      };
      this.setCardError("legalInformation", errors.caeIpi.message);
    }
    if (values.showRepresentatives.length === 0) {
      errors.representatives = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.PublisherRepresentativesLabel,
      };
      this.setCardError("legalInformation", errors.representatives.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "PublisherUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return this.representativeshasChanges || this.managementSocietieshasChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      personType: this.entityValues.personType,
      name: this.entityValues.name,
      maritalStatus: this.entityValues.maritalStatus,
      pseudonym: this.entityValues.pseudonym,
      taxId: this.entityValues.taxId,
      phone: this.entityValues.phone,
      email: this.entityValues.email,
      streetAndNumber: this.entityValues.streetAndNumber,
      floor: this.entityValues.floor,
      suite: this.entityValues.suite,
      postalCode: this.entityValues.postalCode,
      city: this.entityValues.city,
      caeIpi: this.entityValues.caeIpi,
      nationality: this.entityValues.showNationality?.id || null,
      country: this.entityValues.showCountry?.id || null,
      province: this.entityValues.showProvince?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.representativeshasChanges) changed.push("representatives");
    if (this.managementSocietieshasChanges) changed.push("managementSocieties");
    return changed;
  }

  *savePublisher(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadPublisherById(this.savedId);
              } catch (error) {
                console.warn(
                  "[PublisherUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelPublisher(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getPublisherValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = PublisherValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.publisherInstance) {
      const variables = getPublisherUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.publisherUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.publisherUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.publisherUpdate.version_;
      this.saveAttempt = null;
      this.publisherInstance = result.publisherUpdate as Publisher;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadPublisherById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadNationalityValues();
      yield this.loadCountryValues();
      yield this.loadProvinceValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
